import { css, cx } from "emotion";
import { useState, useEffect } from "react";
import { ChannelContext, useCommentsContext } from "../../../comments.context";
import type {
  DropdownStatusDefault,
  CommentChannel,
  AdjudicationStatus,
  ConversationThread
} from "@certa/types";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { get as lodashGet } from "lodash-es";
import { ThreadContainer } from "../ThreadContainer";
import { StatusDropdownView } from "../StatusDropdownView";
import { getEmotionCSSClasses } from "@certa/blocks";
import { threadContainerStyles } from "./SingleChannelView.styles";
import { ThreadListRedesigned } from "../ThreadListRedesigned";
import type { ConversationCommsModel } from "@certa/queries/queries/comms/getConversation.query";
import { useCommsNewEndpoints } from "@certa/common/toggles";
import type { NewThreadResponse } from "@certa/queries/types/comments.types";
import { useCommentsDrawerContext } from "../../../CommentsDrawerContext";

type ChannelViewProps = {
  channel: CommentChannel;
  activeConversationThreadComms: ConversationThread | null;
  defaultActiveThread: number;
  defaultStatus?: DropdownStatusDefault;
  defaultAdjudicationCode?: AdjudicationStatus;
  adjudicationRiskCodes?: string[];
  threadsComms: ConversationCommsModel;
};
/**
 * ChannelView is responsible for showing threads inside a particular
 * channel's context. This mainly houses the tab view for existing threads
 * as well as one to create a new thread.
 */

export const handleOnPrintComment = () => {
  const container = document.querySelector(".single-channel-view");
  const printContents = container?.outerHTML;
  const docHead = document.querySelector("head")?.innerHTML;

  const emotionClasses = getEmotionCSSClasses();

  const body = `
    <!DOCTYPE html>
    <html>
      <head>
      ${docHead}
        <style>
          ${emotionClasses}
          [aria-label="ChevronLeft"],[aria-label="Close"] {
            display : none;
          }
          body {
            height: auto;
            overflow-y: auto !important;
          }
          .comments-new {
            height: auto !important;
            overflow-y: auto;
          }
          @media print {
            .hide-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>${printContents}</body>
    </html>
  `;

  const printWindow = window.open("", "_blank");
  printWindow?.document.write(body);
  printWindow?.document.close();

  if (printWindow) {
    printWindow.onload = () => {
      printWindow?.focus();
      printWindow?.print();
    };
  }
};

const SingleChannelView: React.FC<ChannelViewProps> = props => {
  const [activeThread, setActiveThread] = useState(props.defaultActiveThread); // To control the active thread
  const isCommsNewEndpointEnabled = useCommsNewEndpoints();

  useEffect(
    () => setActiveThread(props.defaultActiveThread),
    [props.defaultActiveThread]
  );

  const { setActiveConversationThreadComms } = useCommentsContext();
  const { shouldShowNewThread, setShouldShowNewThread } =
    useCommentsDrawerContext();

  const {
    channel,
    adjudicationRiskCodes,
    activeConversationThreadComms,
    threadsComms
  } = props;

  const { threads } = channel;

  const toggleNewThread = () => setShouldShowNewThread(!shouldShowNewThread);

  const getCommsConversationId = (threadId: number) => {
    if (!isCommsNewEndpointEnabled || !threadsComms) return;
    const conversation = threadsComms.results.find(
      conversation => conversation.kryptonId === threadId
    );
    if (!conversation) return;
    return conversation.id;
  };

  const onChangeCommsConversation = (commsConversationId: string) => {
    if (!isCommsNewEndpointEnabled || !activeConversationThreadComms) return;

    setActiveConversationThreadComms({
      ...activeConversationThreadComms,
      conversationId: commsConversationId
    });
  };

  const handleThreadChange = (threadId: number) => {
    setActiveThread(threadId);
    setShouldShowNewThread(false);
    const commsConversationId = getCommsConversationId(threadId);
    if (commsConversationId) {
      onChangeCommsConversation(commsConversationId);
    }
  };

  /**
   * Kicks in when the thunk to create a new thread is finished and we need to
   * open the newly created thread tab.
   */
  const handleNewThread = (payload: NewThreadResponse) => {
    // New thread is just created, handle it by making it
    // active tab and hiding the New Thread tab
    const activeKey = lodashGet(payload, "results.0.threads.0.id");
    const newThreadId = lodashGet(payload, "results.0.thread_id");
    const updatedThreads = lodashGet(payload, "results.0.all_threads");
    const newThread = updatedThreads.find(thread => thread.id === newThreadId);

    if (activeKey) {
      setActiveThread(Number(activeKey));
      toggleNewThread();
      if (newThread) {
        onChangeCommsConversation(newThread.comms_conversation_id);
      }
    }
  };

  /**
   * Since the useEffect takes time to update value so using the props directly e
   * meanwhile updated thread id is not saved
   */
  const activeThreadId = threads[activeThread]
    ? activeThread
    : props.defaultActiveThread;

  return (
    <ChannelContext.Provider value={channel}>
      <Stack
        direction="vertical"
        dangerouslySetClassName={cx(
          "single-channel-view",
          css`
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: auto;
            padding: 0;
          `
        )}
      >
        <StatusDropdownView
          threadId={activeThreadId}
          defaultStatus={props.defaultStatus}
          defaultAdjudicationCode={props.defaultAdjudicationCode}
          adjudicationRiskCodes={adjudicationRiskCodes}
        />
        {!shouldShowNewThread && (
          <Stack
            dangerouslySetClassName={cx(
              "hide-print",
              css`
                border-bottom: 1px solid var(--colors-neutral-400);
              `
            )}
            direction="horizontal"
            align="center"
            justify="space-between"
            gap="8"
            padding="12 24"
          >
            <ThreadListRedesigned
              channel={channel}
              selectedThreadId={activeThreadId}
              setActiveThread={handleThreadChange}
            />
          </Stack>
        )}
        <div className={threadContainerStyles}>
          <ThreadContainer
            thread={threads[activeThreadId]}
            activeConversationThreadComms={activeConversationThreadComms}
            showNewThread={shouldShowNewThread}
            onThreadCancel={toggleNewThread}
            onThreadCreate={handleNewThread}
          />
        </div>
      </Stack>
    </ChannelContext.Provider>
  );
};

export { SingleChannelView };
